/**
 * `dm` — yozishma (SPEC §dm, faza 3).
 *
 * **Pufak tomoni `mine` maydonidan** keladi, `sender_id` ni o'zimning
 * ID'im bilan solishtirishdan emas. Sabab: solishtirish uchun "men
 * kimman" ni bilish kerak va u `useMe()` dan keladi — ya'ni xabarlar
 * profil yuklanmaguncha chizilmasdi. Server bu savolga allaqachon
 * javob bergan.
 *
 * Shikoyat ham shu ekranda: **uch bosish** — tugma → sabab → yuborish
 * (faza 3 qabul mezoni).
 *
 * ── Kiritish paneli: nega `position: fixed` EMAS ───────────────────
 * Panel `fixed; bottom: 8px` edi va Telegram ichida **ko'rinmasdi**:
 * `fixed` oyna balandligiga tayanadi, ilova esa `viewportStableHeight`
 * bo'yicha chiziladi (docs/TELEGRAM.md) — ikkalasi teng emas va panel
 * ekran pastidan chiqib ketardi. Klaviatura ochilganda esa u umuman
 * yopilib qolardi.
 *
 * Endi ekran QAT'IY balandlikdagi ustun: xabarlar oqimi o'z ichida
 * suriladi, panel esa oqimning pastida — oddiy element sifatida.
 * Hech narsa "suzmaydi", demak yashirinadigan joy ham yo'q.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { haptic } from '../app/telegram';
import { useBackButton } from '../app/useBackButton';
import { uz } from '../i18n/uz';
import { useMessages, useSendMessage, useThreads, type DmMessageRow } from '../lib/api/useSocial';
import { useRealtimeEvents } from '../lib/realtime/context';
import { Avatar, Icon, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import { ReportSheet } from './ReportSheet';
import './screens.css';

/** Kiritish maydoni shuncha qatorgacha o'sadi, keyin o'zi suriladi. */
const COMPOSER_MAX_HEIGHT = 116;

export function Dm() {
  const { id } = useParams<{ id: string }>();
  const threadId = Number(id);
  const navigate = useNavigate();
  const toast = useToast();

  const threads = useThreads();
  const messages = useMessages(threadId, Number.isInteger(threadId));
  const send = useSendMessage(threadId);

  const [text, setText] = useState('');
  /** Javob berilayotgan xabar — panel ustidagi iqtibos shundan. */
  const [replyTo, setReplyTo] = useState<DmMessageRow | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const goBack = () => navigate('/chats');
  useBackButton(true, goBack);

  const thread = threads.data?.items.find((t) => t.thread_id === threadId);

  // Shu ipga xabar kelsa darhol yangilanadi. Boshqa ipnikini
  // e'tiborsiz qoldiramiz — `chats` ekrani uni o'zi hisoblaydi.
  useRealtimeEvents((event) => {
    if (event.type === 'dm.message' && event.thread_id === threadId) {
      void messages.refetch();
    }
  });

  // Yangi xabar kelganda pastga suriladi.
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.data?.items.length]);

  /* Kiritish maydoni matnga qarab o'sadi.
   *
   * Balandlik AVVAL nolga tushiriladi: `scrollHeight` mavjud balandlikdan
   * kichik bo'la olmaydi, ya'ni tozalamasdan o'lchansa maydon faqat
   * o'sardi va matn o'chirilganda ham katta bo'lib qolardi. */
  useEffect(() => {
    const field = inputRef.current;
    if (!field) return;
    field.style.height = '0px';
    field.style.height = `${Math.min(field.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, [text]);

  async function submit(): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return;

    try {
      await send.mutateAsync(
        replyTo ? { text: trimmed, replyToId: replyTo.id } : { text: trimmed },
      );
      setText('');
      setReplyTo(null);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : uz.dm.closed);
    }
  }

  /** Xabarga bosish — javob berish. Fokus darhol kiritish maydoniga. */
  function startReply(message: DmMessageRow): void {
    setReplyTo(message);
    haptic('light');
    inputRef.current?.focus();
  }

  if (messages.isError) {
    return (
      <div className="screen">
        <div className="empty">{uz.dm.loadFailed}</div>
      </div>
    );
  }

  // Server yangisidan eskisiga qaytaradi — ekranda teskari.
  const rows = [...(messages.data?.items ?? [])].reverse();
  const peerName = thread?.first_name ?? '';

  return (
    <div className="screen dm">
      {toast.message && <Toast message={toast.message} />}

      <div className="screen-bar" style={{ justifyContent: 'flex-start' }}>
        <button type="button" className="icon-btn" onClick={goBack} aria-label={uz.chats.title}>
          <Icon name="back" size={16} />
        </button>

        {thread && (
          <>
            <Avatar
              letter={thread.first_name.slice(0, 1)}
              paletteIdx={thread.palette_idx}
              size={34}
            />
            <div className="dm__peer">
              <div className="dm__peer-name">{thread.first_name}</div>
              {thread.presence !== 'offline' && (
                <div className="dm__peer-state">
                  {thread.presence === 'live'
                    ? uz.chats.presenceLive
                    : thread.presence === 'busy'
                      ? uz.chats.presenceBusy
                      : uz.chats.presenceOnline}
                </div>
              )}
            </div>
          </>
        )}

        <button
          type="button"
          className="icon-btn"
          style={{ marginLeft: 'auto' }}
          onClick={() => setReportOpen(true)}
          aria-label={uz.dm.report}
        >
          <Icon name="shield" size={16} color="var(--fg-55)" />
        </button>
      </div>

      <div className="dm__feed">
        {messages.isLoading ? (
          <div className="empty">{uz.common.loading}</div>
        ) : rows.length === 0 ? (
          <div className="empty">{uz.dm.empty}</div>
        ) : (
          rows.map((message) => (
            <Bubble
              key={message.id}
              message={message}
              peerName={peerName}
              onReply={() => startReply(message)}
            />
          ))
        )}
        <div ref={endRef} />
      </div>

      <div className="dm__composer">
        {/* Javob iqtibosi — kiritish maydonining USTIDA, panel ichida.
            Alohida suzuvchi element bo'lsa u matn yozilayotganda o'z
            joyini yo'qotardi. */}
        {replyTo && (
          <div className="dm__reply">
            <div className="dm__reply-body">
              <span className="dm__reply-who">{replyTo.mine ? uz.dm.replySelf : peerName}</span>
              <span className="dm__reply-text">{replyTo.text ?? ''}</span>
            </div>
            <button
              type="button"
              className="dm__reply-close"
              onClick={() => setReplyTo(null)}
              aria-label={uz.dm.replyCancel}
            >
              <Icon name="close" size={13} color="var(--fg-55)" />
            </button>
          </div>
        )}

        <div className="dm__composer-row blur-input">
          {/* `textarea`, `input` EMAS: uzun xabar bir qatorga sig'masdi
              va boshi ko'rinmay qolardi. Enter YANGI QATOR qo'shadi —
              yuborish faqat tugma bilan. Telegram mobil klaviaturasi
              ham aynan shunday ishlaydi va bu auditoriya uchun tanish
              xatti-harakat. */}
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={uz.dm.placeholder}
            maxLength={1000}
            rows={1}
            enterKeyHint="enter"
            className="dm__input"
          />
          <button
            type="button"
            className="dm__send"
            onClick={() => void submit()}
            disabled={send.isPending || !text.trim()}
            aria-label={uz.dm.send}
          >
            {/* `ic-send` sprite'da YO'Q va sprite yopiq ro'yxat
                (DESIGN_TOKENS §14). `play` eng yaqin ma'noni beradi va
                room chatida ham aynan shu ishlatilgan. */}
            <Icon name="play" size={17} />
          </button>
        </div>
      </div>

      {reportOpen && thread && (
        <ReportSheet
          targetUserId={thread.user_id}
          context="dm"
          onClose={() => setReportOpen(false)}
          onSent={() => toast.show(uz.report.sent)}
          onFailed={() => toast.show(uz.report.failed)}
        />
      )}
    </div>
  );
}

/**
 * Pufak.
 *
 * Butun pufak — tugma: bosilsa o'sha xabarga javob yoziladi. Alohida
 * "javob" ikonkasi har pufakka qo'yilsa ro'yxat shovqinga to'lardi,
 * uzoq bosish (long-press) esa ko'rinmas harakat — uni hech kim
 * topmaydi.
 */
function Bubble({
  message,
  peerName,
  onReply,
}: {
  message: DmMessageRow;
  peerName: string;
  onReply: () => void;
}) {
  const time = new Date(message.created_at).toLocaleTimeString('uz-UZ', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <button
      type="button"
      className={message.mine ? 'bubble bubble--mine' : 'bubble'}
      onClick={onReply}
      aria-label={uz.dm.replyAction}
    >
      {message.reply_to && (
        <span className="bubble__quote">
          <span className="bubble__quote-who">
            {message.reply_to.mine ? uz.dm.replySelf : peerName}
          </span>
          {/* Asl xabar o'chirilgan bo'lsa server `reply_to` ni `null`
              qaytaradi, ya'ni bu yerda matn har doim bor. */}
          <span className="bubble__quote-text">{message.reply_to.text ?? ''}</span>
        </span>
      )}
      <span className="bubble__row">
        <span className="bubble__text">{message.text}</span>
        <span className="bubble__time tnum">{time}</span>
      </span>
    </button>
  );
}
